import { CommonModule } from '@angular/common';
import { ChangeDetectionStrategy, ChangeDetectorRef, Component, Input, OnDestroy, OnInit } from '@angular/core';
import { IProductConfigurableSelection, IProductDataV3 } from '@df/catalog/product/product';
import { ImageModule } from '@df/module-image/image.module';
import { PriceComponent } from '@df/ui/price/price.component';
import { TranslationModule } from '@df/ui/translation/translation.module';
import { ICmsClientModule } from '@icms/client/icms-client.module';
import { Subscription } from 'rxjs';
import { NProductViewLayout, Product } from '../../../product.decorator';
import { ProductViewConfigurableOptionsComponent } from '../../../view-configurable-options/product-view-configurable-options.component';
import { ProductViewCrosssellProductsColorComponent } from '../../../view-crosssell-products/color/product-view-crosssell-products-color.component';
import { NProductViewUpsellProducts } from '../../product-view-upsell-products.namespace';
import { ProductViewUpsellProductsModalComponent } from '../product-view-upsell-products-modal.component';
import { PRODUCT_VIEW_UPSELL_PRODUCT_MODAL__ID } from './product-view-upsell-product-modal.component';

@Component({
    selector: 'product-view-upsell-products-modal-item',
    templateUrl: './product-view-upsell-products-modal-item.html',
    changeDetection: ChangeDetectionStrategy.OnPush,
    standalone: true,
    imports: [
        PriceComponent,
        CommonModule,
        TranslationModule,
        ICmsClientModule,
        ProductViewCrosssellProductsColorComponent,
        ProductViewConfigurableOptionsComponent,
        ImageModule
    ]
})
export class ProductViewUpsellProductsModalItemComponent implements OnDestroy, OnInit {
    hasVisibleOptions = false;
    sourceProductId?: number;
    @Input()
    product?: Product<IProductDataV3>;
    productViewUpsellProductModalId = PRODUCT_VIEW_UPSELL_PRODUCT_MODAL__ID;
    private subscriptions = new Subscription();
    constructor(
        private changeDetectorRef: ChangeDetectorRef,
        private productViewUpsellProductsModal: ProductViewUpsellProductsModalComponent
    ) {}
    ngOnInit(): void {
        this.subscriptions.add(this.productViewUpsellProductsModal.selectionSubject.subscribe(() => this.detectChanges()));
        this.sourceProductId = this.product?.id;
    }
    ngOnDestroy(): void {
        this.subscriptions.unsubscribe();
    }
    get groups(): NProductViewUpsellProducts.IUpsellGroup[] {
        return this.productViewUpsellProductsModal.groups;
    }
    get content(): NProductViewLayout.IBlockDataContent {
        return this.productViewUpsellProductsModal.content;
    }
    toggle(): void {
        this.selected
            ? this.product?.id && this.productViewUpsellProductsModal.unselect(this.product?.id)
            : this.productViewUpsellProductsModal.select(this.product?.id);
        this.detectChanges();
    }
    get selected(): boolean {
        return !!this.product?.id && !!this.productViewUpsellProductsModal.selection?.includes(this.product.id);
    }
    private detectChanges(): void {
        this.changeDetectorRef.detectChanges();
    }

    select = (product: Product<IProductDataV3>, noSelection = false): void => {
        if (!this.selected) {
            if (this.sourceProductId !== product.id) {
                this.productViewUpsellProductsModal.replacements[this.sourceProductId] = product.id;
            } else {
                delete this.productViewUpsellProductsModal.replacements[this.sourceProductId];
            }
            if (product.id !== this.product.id) {
                if (this.selected) {
                    this.productViewUpsellProductsModal.unselect(this.product.id);
                    noSelection && this.productViewUpsellProductsModal.select(product.id);
                }
                this.product = product;
            }
            !noSelection && this.productViewUpsellProductsModal.select(product.id);
            this.detectChanges();
        }
    };

    /**
     * Handles configurable change
     */
    onSelectionChange(filterModel: IProductConfigurableSelection): void {
        const newProduct = this.product?.parent.matchChild(filterModel) as Product<IProductDataV3>;
        if (newProduct) {
            this.select(newProduct, true);
        }
    }

    onProductConfigurablesInit($event) {
        const vm = $event.vm as ProductViewConfigurableOptionsComponent;
        this.hasVisibleOptions = !!vm.visibleAttributes?.length;
        this.detectChanges();
    }

    onCrossellProductClick(product: Product<IProductDataV3>): void {
        this.content.product = product;
        this.select(product, true);
        this.detectChanges();
    }

    get displayColorCrossSells(): boolean {
        if (this.product?.hasColorCrosssells) {
            if (this.product?.parent?.isConfigurable) {
                return !this.hasVisibleOptions;
            }
            return true;
        }
        return false;
    }
}
